import { inject, type ApplicationConfig, ErrorHandler, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withPreloading, withRouterConfig, withViewTransitions } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { Events, injectDispatch, provideDispatcher } from '@ngrx/signals/events';
import { firstValueFrom, merge, of, first, timeout } from 'rxjs';
import { appRoutes } from './app.routes';
import { ActivePortalPreloadStrategy, cognitoAuthInterceptor, idempotencyKeyInterceptor, territoryContextInterceptor } from '@vantage-web/auth';
import { provideVantageI18n } from '@vantage-web/shared-i18n';

import { APP_CONFIG, applyRuntimeConfig, authEvents, AuthStore, GlobalErrorHandler, httpCacheInterceptor, provideTheme, VANTAGE_APP_CONFIG } from '@vantage-web/core-data';

/**
 * Mutable config holder seeded from the build-time bundle (issue #1). The app-initializer below
 * fetches `/config.json` (rendered by the container entrypoint from the same SSM values the
 * backend uses) and merges any usable values over this object IN PLACE — the reference stays
 * stable, so `ConfigService` (which caches `inject(APP_CONFIG)`) observes the result. With no file
 * present (local `nx serve`, e2e, or a container without the entrypoint) the bundled fallback is
 * used unchanged, so local dev stays frictionless and there is never a hard dependency on AWS.
 */
const runtimeAppConfig = { ...VANTAGE_APP_CONFIG };

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      // Angular 22 changed the default to 'always', which hands every ancestor route's params, data and
      // resolves to child routes; with withComponentInputBinding() above, those would start binding to
      // same-named inputs on child pages. 'emptyOnly' is the 21.2 behaviour this app was built and tested on.
      withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' }),
      withViewTransitions(),
      withPreloading(ActivePortalPreloadStrategy),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
    ),
    // idempotencyKeyInterceptor MUST run upstream of cognitoAuthInterceptor: on a 401 the auth
    // interceptor retries by cloning the REQUEST IT RECEIVED, so the Idempotency-Key has to already
    // be on that request when auth sees it — otherwise the retry mints a fresh key and the one
    // retry path the header exists for is the one path where it silently changes.
    provideHttpClient(withInterceptors([idempotencyKeyInterceptor, cognitoAuthInterceptor, territoryContextInterceptor, httpCacheInterceptor])),
    provideDispatcher(),
    provideVantageI18n(),
    provideTheme(),
    { provide: APP_CONFIG, useValue: runtimeAppConfig },
    provideAppInitializer(async () => {
      // Acquire every injection-context dependency SYNCHRONOUSLY, before the first `await` below.
      // Angular runs this factory via `runInInjectionContext`, whose injector is torn down in a
      // synchronous `finally` the instant the async fn yields at an await — so any inject() /
      // injectDispatch() placed AFTER the await throws NG0203 and fails bootstrap for every user.
      // Instantiating AuthStore here also subscribes its restoreSession$ handler; it reads no config
      // at construction (config is read lazily when restoreSession is dispatched), so acquiring it
      // before the config fetch is safe.
      inject(AuthStore); // instantiate the root store so its restoreSession$ handler is subscribed
      const events = inject(Events);
      const dispatch = injectDispatch(authEvents);

      // Absolute path (base href is "/") so the fetch is route-independent; `no-store` so a Cognito
      // pool recreate self-heals on the next container restart. Never block bootstrap on failure: a
      // missing /config.json returns a 404 (res.ok === false) and the bundled fallback is kept; the
      // catch only covers a true network failure (offline / DNS / CSP).
      //
      // BOUNDED for the same reason the session restore below is (VG-F-177): the initializer promise
      // is what Angular bootstrap awaits, so a socket that neither resolves nor errors — captive
      // portal, proxy blackhole, half-open ALB — would leave bootstrap pending forever behind a
      // white screen with nothing logged. `AbortSignal.timeout` turns that case into a rejection the
      // catch below already handles, so a hang degrades exactly like the 404 path; the 8s budget is
      // the one the restore timeout uses. Fail-open semantics are unchanged.
      try {
        const res = await fetch('/config.json', { cache: 'no-store', signal: AbortSignal.timeout(8000) });
        if (res.ok) applyRuntimeConfig(runtimeAppConfig, await res.json());
      } catch {
        // Offline / network failure / abort on the 8s budget — keep the bundled fallback.
      }

      // Rehydrate the auth session from persisted tokens BEFORE the router's initial navigation,
      // so route guards evaluate against a hydrated AuthStore instead of the empty initial state.
      // Without this, every full page load (Refresh / Back) starts at `isAuthenticated:false` and
      // the guards bounce a logged-in user to `/auth/login`. Dispatched after the config fetch above
      // because the real-mode restore reads Cognito config + `mockAuth` from ConfigService, which is
      // backed by the in-place-mutated `runtimeAppConfig`.
      //
      // Subscribe to the settle events BEFORE dispatching: NgRx Events are delivered on the
      // queueScheduler (asynchronously), so the subscription must be live before the dispatch or the
      // emission is lost. The 8s timeout is fail-open (substitutes `of(null)`, never throws) so a
      // hung token refresh can never freeze bootstrap — the guard then just routes to login.
      const settled = firstValueFrom(
        merge(events.on(authEvents.restoreSessionSuccess), events.on(authEvents.restoreSessionFailed)).pipe(
          first(),
          timeout({ first: 8000, with: () => of(null) }),
        ),
      );
      dispatch.restoreSession();
      await settled;
    }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
};
